import React, { FC, CSSProperties } from 'react'
import queryString from 'query-string'
import { Navigate, useLocation } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import { routes } from 'config'
import { constants } from 'utils'
import axios from 'axios'
import { BaseLayout, Module } from 'components'
import { LoggingOut, LoggedOut } from './components'
// import { CSSProperties } from '@mui/material/styles/withStyles'

const Logout: FC = () => {
  const { authState, oktaAuth } = useOktaAuth()
  const location = useLocation()

  const devLogoutUrls = [
    'https://okta.dev.brightmls.com/login/signout',
    'https://www.dev.brightmls.com/logout?silent=1',
    'https://matrix.dev.brightmls.com/Matrix/logout.aspx',
    'https://apps.dev.brightmls.com/financial/logout',
    'https://lmsedit.dev.brightmls.com/KeyAppSvc/saml/logout',
    'https://apps.dev.brightmls.com/permissions/logout',
    'https://apps.dev.brightmls.com/listingDocuments/logout',
    'https://apps.dev.brightmls.com/publicRecordsWeb/logout',
    'https://brightmls--devmerge.sandbox.my.site.com/customers/secur/logout.jsp', // this doesn't really work because Salesforce doesn't work in DEV but adding for completeness if it's ever fixed
    'https://teams.dev.brightmls.com/logout?silent=1',
    'https://insights.dev.brightmls.com/logout?silent=1',
    'https://brokerapps.dev.brightmls.com/api/logout',
    'https://subscriptionapps.dev.brightmls.com/api/logout',
    'https://showing.dev.brightmls.com/logout',
    'https://laeservices.dev.brightmls.com/auth/logout?silent=1',
  ]

  const tstLogoutUrls = [
    'https://okta.tst.brightmls.com/login/signout',
    'https://www.tst.brightmls.com/logout?silent=1',
    'https://matrix.tst.brightmls.com/Matrix/logout.aspx',
    'https://apps.tst.brightmls.com/financial/logout',
    'https://lmsedit.tst.brightmls.com/KeyAppSvc/saml/logout',
    'https://apps.tst.brightmls.com/permissions/logout',
    'https://apps.tst.brightmls.com/listingDocuments/logout',
    'https://apps.tst.brightmls.com/publicRecordsWeb/logout',
    'https://brightmls--stage.sandbox.my.site.com/customers/secur/logout.jsp',
    'https://teams.tst.brightmls.com/logout?silent=1',
    'https://insights.tst.brightmls.com/logout?silent=1',
    'https://brokerapps.tst.brightmls.com/api/logout',
    'https://subscriptionapps.tst.brightmls.com/api/logout',
    'https://showing.tst.brightmls.com/logout',
    'https://laeservices.tst.brightmls.com/auth/logout?silent=1',
  ]

  const prdLogoutUrls = [
    'https://okta.brightmls.com/login/signout',
    'https://www.brightmls.com/logout?silent=1',
    'https://matrix.brightmls.com/Matrix/logout.aspx',
    'https://apps.brightmls.com/financial/logout',
    'https://apps.brightmls.com/permissions/logout',
    'https://apps.brightmls.com/listingDocuments/logout',
    'https://apps.brightmls.com/publicRecordsWeb/logout',
    'https://brightmls.my.site.com/customers/secur/logout.jsp',
    'https://support.brightmls.com/secur/logout.jsp', // this application is in production only
    'https://lmsedit.brightmls.com/KeyAppSvc/saml/logout',
    'https://teams.brightmls.com/logout?silent=1',
    'https://insights.brightmls.com/logout?silent=1',
    'https://brokerapps.brightmls.com/api/logout',
    'https://subscriptionapps.brightmls.com/api/logout',
    'https://showing.brightmls.com/logout',
    'https://laeservices.brightmls.com/auth/logout?silent=1',
  ]

  // as per MLSB-3534, special care needs to be taken when logging out of Salesforce
  // Salesforce logout page after logging out of Salesforce must redirect back to /logout
  // since /logout calls Salesforce logout page from iFrame, that redirect from Salesforce to /logout will be in that iFrame
  // if /logout page determines it's in an iframe then it means it was redirected from Salesforce logout page
  // if that's true, then stop processing to avoid a logout loop
  if (window !== window.top) return null

  let logoutUrlsToUse = prdLogoutUrls
  switch (process.env.REACT_APP_LOGOUT_ENVIRONMENT) {
    case 'local':
      logoutUrlsToUse = devLogoutUrls
      break
    case 'dev':
      logoutUrlsToUse = devLogoutUrls
      break
    case 'tst':
      logoutUrlsToUse = tstLogoutUrls
      break
  }

  // Clean up session cookies
  sessionStorage.removeItem(
    constants.BROWSER_STORAGE_KEY.LOGGING_OPERATION_DONE,
  )
  localStorage.removeItem('accessToken')
  const values = queryString.parse(location.search)
  let isLoggedOutOperationDone = false
  if (values.state != null && values.state === 'complete') {
    isLoggedOutOperationDone = true
  }

  const iframeStyle: CSSProperties = {
    width: '1px',
    height: '1px',
    visibility: 'hidden',
  }

  interface loadMap {
    [frameName: string]: boolean
  }
  let frameLoadState: loadMap = {}

  const logout = async () => {
    try {
      await axios.delete(
        `https://${process.env.REACT_APP_OKTA_DOMAIN}/api/v1/sessions/me`,
        { withCredentials: true },
      )
    } catch (e: any) {
      if (e.response && e.response.status === 404) {
        console.log('Session was not found to delete for logout')
      } else {
        console.error(e)
      }
    } // Ignore errors - this method will return a 404 if there is no Okta session

    if (authState?.isAuthenticated) {
      await oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin + routes.logout,
        state: 'complete',
      })
      localStorage.removeItem('okta-cache-storage')
      localStorage.removeItem('okta-original-uri-storage')
      localStorage.removeItem('okta-shared-transaction-storage')
    } else {
      window.location.href =
        window.location.origin + routes.logout + '?state=complete'
    }
  }

  const frameLoaded = async (
    event: React.SyntheticEvent<HTMLIFrameElement, Event>,
  ) => {
    frameLoadState[event.currentTarget.src] = true

    // Check if we've finiaed loading all iFrames
    let allLoaded = true
    let frameSrc: string
    for (frameSrc of logoutUrlsToUse) {
      if (!frameLoadState[frameSrc]) {
        allLoaded = false
        break
      }
    }
    if (allLoaded) {
      // Now that we've attempted to log out of every application (ignoring any error responses)
      // log out of the login application and the IdP itself
      setTimeout(function () {
        console.log('Waiting for all to logout')
        logout()
      }, 1500)
    }
  }

  return (
    <BaseLayout>
      <Module>
        {(() => {
          if (
            sessionStorage.getItem(
              constants.BROWSER_STORAGE_KEY.IS_INACTIVE_MEMBER,
            )
          ) {
            return <Navigate to={{ pathname: routes.membershipExpired }} />
          } else if (isLoggedOutOperationDone) {
            return <LoggedOut />
          } else {
            return (
              <div>
                <LoggingOut />
                {logoutUrlsToUse.map((url, index) => (
                  <iframe
                    title={`LogoutFrame${index}`}
                    key={`frame${index}`}
                    style={iframeStyle}
                    src={url}
                    onLoad={frameLoaded}
                  />
                ))}
              </div>
            )
          }
        })()}
      </Module>
    </BaseLayout>
  )
}

export default Logout
